import { DictationRefs, DictationSetters, resetAllStates } from "./dictation-refs.utils";
import {
  UseDictationOptions,
  handleTranscriptMessage,
  buildWebSocketUrl,
  validateSecureContext,
  validateAuthToken,
  setupPCMRecording,
  setupMediaRecorderRecording,
} from "./dictation-recording.utils";
import { getBackendDocumentId } from "@/utils/document-id.utils";

export interface WebSocketHandlerParams {
  options: UseDictationOptions;
  usePCMFormat: boolean;
  refs: DictationRefs;
  setters: DictationSetters;
  user: { id?: string } | undefined;
  doc: { _id?: string; id?: string } | undefined;
  cleanupAudio: () => void;
}

export function attachWebSocketHandlers(socket: WebSocket, params: WebSocketHandlerParams): void {
  const { options, usePCMFormat, refs, setters, user, doc, cleanupAudio } = params;

  socket.onopen = () => {
    console.log("[DictationDebug] WebSocket connected");
    const documentId = getBackendDocumentId(doc);
    const defaultParams = {
      user: user?.id,
      document: documentId != null ? [documentId] : [],
      current_document: documentId,
    };
    const socketParams = options.socketParams
      ? { ...defaultParams, ...options.socketParams }
      : defaultParams;
    console.log("[DictationDebug] WebSocket onopen: Sending initial parameters:", socketParams);
    refs.socketRef.current?.send(JSON.stringify(socketParams));

    if (!usePCMFormat && refs.mediaRecorderRef.current) {
      refs.mediaRecorderRef.current.start(1000);
    } else {
      setters.setIsMicroLoading(false);
      refs.isMicroLoadingRef.current = false;
      setters.setIsRecording(true);
      refs.isRecordingRef.current = true;
    }
  };

  socket.onmessage = (event) => {
    console.log("[DictationDebug] Received text:", event.data);
    handleTranscriptMessage(event.data, setters.setTranscript, options.onTranscript);
  };

  socket.onclose = () => {
    console.log("[DictationDebug] WebSocket disconnected");
    resetAllStates(refs, setters);
    cleanupAudio();
  };

  socket.onerror = (error) => {
    console.error("[DictationDebug] WebSocket error:", error);
    resetAllStates(refs, setters);
    cleanupAudio();
  };
}

export async function executeStartRecording(
  options: UseDictationOptions,
  usePCMFormat: boolean,
  refs: DictationRefs,
  setters: DictationSetters,
  wsHandlerParams: WebSocketHandlerParams,
  cleanupAudio: () => void
): Promise<void> {
  if (refs.isRecordingRef.current || refs.isMicroLoadingRef.current) return;

  setters.setIsMicroLoading(true);
  refs.isMicroLoadingRef.current = true;
  console.log("[DictationDebug] startRecording: Setting isMicroLoading to true");

  if (!validateSecureContext(refs, setters.setIsMicroLoading)) return;

  try {
    const token = validateAuthToken(refs, setters.setIsMicroLoading);
    if (token == null) return;

    if (
      typeof Office !== "undefined" &&
      Office.context?.platform === Office.PlatformType.OfficeOnline &&
      Office.devicePermission != null
    ) {
      await Office.devicePermission.requestPermissions([Office.DevicePermissionType.microphone]);
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Stop may have run while the permission prompt was open. Do not keep the
    // new stream if the UI already asked us to tear down.
    if (!refs.isMicroLoadingRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    refs.audioStreamRef.current = stream;

    refs.socketRef.current = new WebSocket(buildWebSocketUrl(options, token));
    attachWebSocketHandlers(refs.socketRef.current, wsHandlerParams);

    if (usePCMFormat) {
      setupPCMRecording(stream, refs, setters.setIsSpeaking);
    } else {
      setupMediaRecorderRecording(stream, refs, setters, cleanupAudio);
    }
  } catch (error) {
    console.error("[DictationDebug] Error starting recording:", error);
    cleanupAudio();
    resetAllStates(refs, setters);
  }
}

export function executeStopRecording(
  usePCMFormat: boolean,
  refs: DictationRefs,
  setters: DictationSetters,
  cleanupAudio: () => void
): void {
  console.log("[DictationDebug] stopRecording called");
  if (usePCMFormat) {
    if (refs.isRecordingRef.current) setters.setIsConverting(true);
    cleanupAudio();
    resetAllStates(refs, setters);
  } else {
    if (refs.mediaRecorderRef.current?.state === "recording") {
      refs.mediaRecorderRef.current.stop();
    }
  }
}
